/**
 * EMR Section Actions Group Component
 *
 * Renders a collection of action buttons for section or item level.
 * Filters actions by scope and handles dialog state management.
 *
 * Single Responsibility: Render action buttons collection
 * Uses useEmrActionHandler hook for all business logic
 */

import React, { useMemo } from 'react'
import { Box } from '@mui/material'
import useEmrActionHandler from '../hooks/useEmrActionHandler'
import EmrSectionActionButton from './EmrSectionActionButton'
import EmrActionDialog from './EmrActionDialog'

export default function EmrSectionActionsGroup({
  actions = [],
  scope = 'section', // "section" | "item"
  context = {}, // { item, sectionData, runtime }
  practitionerRole = null,
  runtime = {},
  sectionData = null,
  setSectionData = null,
}) {
  // Initialize action handler hook
  const actionHandler = useEmrActionHandler({
    runtime,
    practitionerRole,
    sectionData,
    setSectionData,
  })

  /**
   * Filter actions by scope
   * If action.scope is explicitly set, use that; otherwise infer from context
   */
  const filteredActions = useMemo(() => {
    return actions.filter((action) => {
      // Skip guard actions - they're not user-triggered
      if (action.kind === 'guard') {
        return false
      }

      // Check explicit scope
      if (action.scope) {
        return action.scope === scope
      }

      // Infer scope: if we have an item in context, assume item-level
      // Otherwise assume section-level
      if (scope === 'item') {
        return !!context.item
      }

      return true
    })
  }, [actions, scope, context])

  /**
   * Get visible actions
   */
  const visibleActions = useMemo(() => {
    return filteredActions.filter((action) => actionHandler.shouldShowAction(action, context))
  }, [filteredActions, actionHandler, context])

  // Don't render if no visible actions
  if (visibleActions.length === 0) {
    return null
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        {visibleActions.map((action, index) => (
          <EmrSectionActionButton
            key={action.id || `action-${index}`}
            action={action}
            context={context}
            onExecute={actionHandler.executeAction}
            shouldShow={actionHandler.shouldShowAction(action, context)}
            isEnabled={actionHandler.isActionEnabled(action, context)}
          />
        ))}
      </Box>

      {/* Dialog for actions that require user input */}
      <EmrActionDialog
        open={actionHandler.dialogState.open}
        onClose={actionHandler.closeDialog}
        dialogDef={actionHandler.dialogState.action?.dialog}
        onSubmit={actionHandler.handleDialogSubmit}
      />
    </>
  )
}
